/**
 * Ads Manager deep links (spec §4, FR-D2-09, ruling R18): one URL template per
 * ad channel, built from the account's and the campaign's native ids. Every
 * template ships disabled until the owner confirms it (R18). The page shows a
 * campaign's native id, when it has one, with a copy button; ↗ appears only when
 * adsManagerUrlOf gives a URL. Pure.
 *
 * @module services/channels/adsManagerLinks
 */
import { isAdChannel, type AdChannel } from './channelTypes';

export interface AdsManagerTemplate {
  enabled: boolean;
  build: (ids: { account: string; campaign: string }) => string;
}

/** A registry of the ADS_MANAGER shape (the helpers take one, so the tests can pass one with flags of their own). */
type Registry = Readonly<Record<AdChannel, AdsManagerTemplate>>;

const enc = encodeURIComponent;

/** R18: the candidate templates. Set `enabled` only for one the owner has confirmed. */
export const ADS_MANAGER: Readonly<Record<AdChannel, AdsManagerTemplate>> = {
  facebook: {
    enabled: false,
    build: ({ account, campaign }) =>
      `https://adsmanager.facebook.com/adsmanager/manage/campaigns?act=${enc(account.replace(/^act_/, ''))}&selected_campaign_ids=${enc(campaign)}`,
  },
  tiktok: {
    enabled: false,
    build: ({ account, campaign }) => `https://ads.tiktok.com/i18n/perf/campaign?aadvid=${enc(account)}&keyword=${enc(campaign)}`,
  },
  google: {
    enabled: false,
    build: ({ account, campaign }) => `https://ads.google.com/aw/adgroups?campaignId=${enc(campaign)}&__e=${enc(account.replace(/-/g, ''))}`,
  },
  line: {
    enabled: false,
    build: ({ account, campaign }) => `https://admanager.line.biz/adaccount/${enc(account)}/campaign/${enc(campaign)}/adgroup`,
  },
};

/**
 * The ids come as the reader hands them over (channelEntityOf, M3): trimmed, and a blank one is null. A missing
 * id would build a link to nowhere.
 */
const candidateFrom = (
  registry: Registry, platform: string, accountExternalId: string | null, campaignExternalId: string | null,
): string | null =>
  (isAdChannel(platform) && accountExternalId !== null && campaignExternalId !== null
    ? registry[platform].build({ account: accountExternalId, campaign: campaignExternalId })
    : null);

/**
 * @internal The ↗ URL that `registry` gives. Exported only so the tests can
 * drive an enabled template (every shipped one is disabled); the page calls
 * adsManagerUrlOf.
 */
export const adsManagerUrlFrom = (
  registry: Registry, platform: string, accountExternalId: string | null, campaignExternalId: string | null,
): string | null =>
  isAdChannel(platform) && registry[platform].enabled
    ? candidateFrom(registry, platform, accountExternalId, campaignExternalId)
    : null;

/** The candidate URL whatever `enabled` says: the owner checks it before confirming the template (R18). null for a non-ad platform or a missing id. */
export const adsManagerCandidateOf = (
  platform: string, accountExternalId: string | null, campaignExternalId: string | null,
): string | null => candidateFrom(ADS_MANAGER, platform, accountExternalId, campaignExternalId);

/** ↗: the candidate only while its template is enabled, else null (R18). */
export const adsManagerUrlOf = (
  platform: string, accountExternalId: string | null, campaignExternalId: string | null,
): string | null => adsManagerUrlFrom(ADS_MANAGER, platform, accountExternalId, campaignExternalId);
